import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMobileMenu } from '../hooks/useMobileMenu';

type InfoPageKind =
  | 'about'
  | 'contact'
  | 'careers'
  | 'help'
  | 'safety'
  | 'report'
  | 'terms'
  | 'privacy'
  | 'sitemap';

interface InfoPageProps {
  kind: InfoPageKind;
}

interface InfoPageContent {
  title: string;
  description: string;
  sections: Array<{ heading: string; paragraphs: string[] }>;
}

const SUPPORT_EMAIL = 'support@adsworld24.com';
const SUPPORT_PHONE = '+92 302 4333398';

const PAGE_CONTENT: Record<InfoPageKind, InfoPageContent> = {
  about: {
    title: 'About AdsWorld',
    description: 'AdsWorld helps people across Pakistan discover, buy, and sell with confidence.',
    sections: [
      { heading: 'Our marketplace', paragraphs: ['We connect local buyers and sellers across vehicles, property, mobiles, jobs, and everyday items.'] },
      { heading: 'Built for trust', paragraphs: ['Clear listings, seller tools, and reporting options help the community make informed decisions.'] },
    ],
  },
  contact: {
    title: 'Contact us',
    description: 'Our support team is available for account, listing, and marketplace questions.',
    sections: [
      { heading: 'Support', paragraphs: ['For help with an account or listing, contact our support team using the details below.'] },
      { heading: 'Response times', paragraphs: ['We review support requests during business hours and aim to respond as soon as possible.'] },
    ],
  },
  careers: {
    title: 'Careers',
    description: 'Help build a safer, simpler marketplace for Pakistan.',
    sections: [
      { heading: 'Work with us', paragraphs: ['We are always interested in meeting people who care about useful, inclusive technology.'] },
      { heading: 'Open roles', paragraphs: ['There are no published roles at the moment. Send your profile to our support email to be considered for future opportunities.'] },
    ],
  },
  help: {
    title: 'Help Center',
    description: 'Quick answers for buying, selling, and managing your AdsWorld account.',
    sections: [
      { heading: 'Buying', paragraphs: ['Use search and category filters to find listings, then contact the seller through the listing page.'] },
      { heading: 'Selling', paragraphs: ['Create an account, choose the most specific category, and add clear photos and accurate details to your ad.'] },
      { heading: 'Account support', paragraphs: ['Use the contact page if you cannot access your account or need help with a listing.'] },
    ],
  },
  safety: {
    title: 'Safety Tips',
    description: 'Use these practical steps to make marketplace transactions safer.',
    sections: [
      { heading: 'Meet safely', paragraphs: ['Meet in a public place when possible and take someone with you for high-value transactions.'] },
      { heading: 'Check before paying', paragraphs: ['Inspect the item and confirm important details before sending payment or sharing personal information.'] },
      { heading: 'Report concerns', paragraphs: ['Report suspicious, misleading, or prohibited listings so our team can review them.'] },
    ],
  },
  report: {
    title: 'Report an ad',
    description: 'Help keep AdsWorld useful and safe by reporting suspicious or misleading listings.',
    sections: [
      { heading: 'What to report', paragraphs: ['Report scams, prohibited items, misleading information, duplicate listings, or abusive content.'] },
      { heading: 'How to report', paragraphs: ['Open the listing and use its report option. If that option is unavailable, email support with the listing URL and a short explanation.'] },
    ],
  },
  terms: {
    title: 'Terms of Service',
    description: 'The rules for using AdsWorld and participating in its marketplace.',
    sections: [
      { heading: 'Using AdsWorld', paragraphs: ['You must provide accurate information, use the service lawfully, and respect other community members.'] },
      { heading: 'Listings and transactions', paragraphs: ['Sellers are responsible for their listings. Buyers and sellers are responsible for agreeing transaction terms directly.'] },
      { heading: 'Enforcement', paragraphs: ['We may remove content or restrict accounts that violate these terms or create a risk for the community.'] },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'How AdsWorld handles account information, listings, and privacy choices.',
    sections: [
      { heading: 'Information we use', paragraphs: ['We use information you provide to operate your account, publish your listings, and provide support.'] },
      { heading: 'Location and cookies', paragraphs: ['Your device location is requested only after you choose to use it for nearby results. You can manage non-essential cookie choices from Privacy Settings.'] },
      { heading: 'Your choices', paragraphs: ['You can update your profile, manage cookie choices, and contact support about your personal information.'] },
    ],
  },
  sitemap: {
    title: 'Sitemap',
    description: 'Find the main sections of AdsWorld.',
    sections: [
      { heading: 'Marketplace', paragraphs: ['Browse categories, search listings, or post an ad after signing in.'] },
      { heading: 'Support and company', paragraphs: ['Use the links below to find help, safety guidance, company information, and policies.'] },
    ],
  },
};

const SITEMAP_LINKS = [
  { label: 'Browse categories', to: '/categories' },
  { label: 'About us', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'Help Center', to: '/help-center' },
  { label: 'Safety Tips', to: '/safety-tips' },
  { label: 'Report an ad', to: '/report-ad' },
  { label: 'Terms of Service', to: '/terms' },
  { label: 'Privacy Policy', to: '/privacy' },
];

const PAGE_PATHS: Record<InfoPageKind, string> = {
  about: '/about',
  contact: '/contact',
  careers: '/careers',
  help: '/help-center',
  safety: '/safety-tips',
  report: '/report-ad',
  terms: '/terms',
  privacy: '/privacy',
  sitemap: '/sitemap',
};

export default function InfoPage({ kind }: InfoPageProps): JSX.Element {
  const { mobileOpen, toggleMobile } = useMobileMenu();
  const content = PAGE_CONTENT[kind];
  const canonicalUrl = `https://adsworld24.com${PAGE_PATHS[kind]}`;

  useEffect(() => {
    document.title = `${content.title} | AdsWorld`;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = canonicalUrl;
  }, [canonicalUrl, content.title]);

  return (
    <div className="min-h-screen bg-surface">
      <Helmet>
        <title>{content.title} | AdsWorld</title>
        <meta name="description" content={content.description} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:py-16">
        <Link to="/" className="text-sm font-semibold text-primary-700 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2">
          Back to home
        </Link>
        <header className="mt-6 border-b border-dark-200 pb-8">
          <h1 className="text-3xl font-black text-dark-900 sm:text-4xl">{content.title}</h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-dark-700">{content.description}</p>
        </header>
        <div className="space-y-10 py-10">
          {content.sections.map((section) => (
            <section key={section.heading} aria-labelledby={`${kind}-${section.heading.replace(/\s+/g, '-').toLowerCase()}`}>
              <h2 id={`${kind}-${section.heading.replace(/\s+/g, '-').toLowerCase()}`} className="text-xl font-bold text-dark-900">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph} className="mt-3 leading-relaxed text-dark-700">{paragraph}</p>)}
            </section>
          ))}
          {kind === 'contact' && (
            <section aria-labelledby="contact-details">
              <h2 id="contact-details" className="text-xl font-bold text-dark-900">Contact details</h2>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-6">
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex min-h-11 items-center gap-2 font-semibold text-primary-700 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"><Mail className="h-5 w-5" />{SUPPORT_EMAIL}</a>
                <a href="tel:+923024333398" className="inline-flex min-h-11 items-center gap-2 font-semibold text-primary-700 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"><Phone className="h-5 w-5" />{SUPPORT_PHONE}</a>
              </div>
            </section>
          )}
          {kind === 'sitemap' && (
            <nav aria-label="Sitemap links" className="grid gap-3 sm:grid-cols-2">
              {SITEMAP_LINKS.map((link) => <Link key={link.to} to={link.to} className="min-h-11 rounded-lg border border-dark-200 px-4 py-3 font-semibold text-primary-700 hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2">{link.label}</Link>)}
            </nav>
          )}
        </div>
      </main>
      <Footer onOpenPrivacySettings={() => {}} />
    </div>
  );
}